import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";
import axios from "axios";
import { fmtPrice } from "../lib/format";

export default function ShareCardPublic() {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/share/${cardId}`)
      .then((r) => setCard(r.data))
      .catch(() => setError(true));
  }, [cardId]);

  return (
    <div className="min-h-screen bg-background noise-bg py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="share-brand-link">
            <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-black tracking-tighter text-lg text-white">
              SphereInvest<span className="text-primary"> AI</span>
            </span>
          </Link>
          <span className="overline-label">Public research card</span>
        </div>

        {error && (
          <div className="widget p-10 text-center" data-testid="share-card-not-found">
            <p className="text-muted-foreground">This research card does not exist or was revoked by its owner.</p>
            <Link to="/login" className="text-primary hover:underline text-sm mt-4 inline-block">Go to SphereInvest AI</Link>
          </div>
        )}

        {card && (
          <div className="widget p-8 space-y-6 fade-up" data-testid="share-card-content">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-white">{card.ticker}</h1>
                <p className="text-muted-foreground mt-1">{card.name} · {card.sector}</p>
              </div>
              <div className="text-right">
                <div className="font-mono text-2xl text-white">{fmtPrice(card.price, card.currency)}</div>
                <div className="text-[11px] font-mono text-muted-foreground">
                  AI summary generated {new Date(card.summary_generated_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-gain" />
                  <span className="overline-label text-gain">Attractive factors</span>
                </div>
                <ul className="space-y-2">
                  {card.summary.attractive_factors.map((f, i) => (
                    <li key={i} className="text-sm text-gray-100 border-l-2 border-gain/50 pl-3 leading-relaxed">{f}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-loss" />
                  <span className="overline-label text-loss">Risk factors</span>
                </div>
                <ul className="space-y-2">
                  {card.summary.risk_factors.map((f, i) => (
                    <li key={i} className="text-sm text-gray-100 border-l-2 border-loss/50 pl-3 leading-relaxed">{f}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <span className="overline-label">Valuation view</span>
                <p className="text-sm text-gray-300 mt-2 leading-relaxed">{card.summary.valuation_view}</p>
              </div>
              <div>
                <span className="overline-label">Technical view</span>
                <p className="text-sm text-gray-300 mt-2 leading-relaxed">{card.summary.technical_view}</p>
              </div>
            </div>

            <p className="text-sm text-gray-400 italic border border-border rounded-sm p-3">
              {card.summary.verdict_style_note}
            </p>

            <p className="text-xs text-muted-foreground pt-4 border-t border-white/10" data-testid="share-card-disclaimer">
              This is AI-generated research, not financial advice. Snapshot frozen at share time — data may be outdated.
            </p>
          </div>
        )}

        <div className="widget p-6 flex items-center justify-between flex-wrap gap-4 bg-blue-950/20 border-primary/20">
          <div>
            <p className="font-heading font-bold text-white tracking-tight">Research 70+ US &amp; UK large caps yourself</p>
            <p className="text-xs text-muted-foreground mt-1">Scanner presets, technicals, peer valuations and AI research summaries.</p>
          </div>
          <Link to="/login?mode=register" data-testid="share-signup-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/85 text-white text-sm rounded-sm transition-colors duration-150">
            Create your free account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
